import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import {
  buildRequestListUrl,
  isAgentValue,
  isRequestTypeKey,
  REQUEST_TYPE_BY_KEY,
} from '@/constants/Systemkey/request-type';
import type {
  AgentValue,
  RequestTypeConfig,
} from '@/constants/Systemkey/request-type';
import ROUTER from '@/routerMap/ROUTER';

export interface RequestNavigation {
  locationKey: string;
  config: RequestTypeConfig;
  agent: AgentValue;
  listUrl: string;
  recordId?: number;
}

const Context = createContext<{
  navigation: RequestNavigation | null;
  setNavigation: (value: RequestNavigation) => void;
}>({ navigation: null, setNavigation: () => {} });

export function RequestNavigationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [navigation, setNavigation] = useState<RequestNavigation | null>(null);
  const value = useMemo(() => ({ navigation, setNavigation }), [navigation]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useRequestNavigation() {
  const { navigation, setNavigation } = useContext(Context);
  const location = useLocation();
  const locationKey = location.pathname + location.search;
  if (navigation?.locationKey === locationKey)
    return { navigation, setNavigation };
  // List/Create context is known without loading a record. Record pages must wait for the API.
  const params = new URLSearchParams(location.search);
  const key = params.get('requestType');
  const agent = params.get('agent');
  const inferred: RequestNavigation | null =
    [ROUTER.REQUEST_MANAGEMENT, ROUTER.REQUEST_MANAGEMENT_CREATE].includes(
      location.pathname,
    ) &&
    isRequestTypeKey(key) &&
    isAgentValue(agent)
      ? {
          locationKey,
          config: REQUEST_TYPE_BY_KEY[key],
          agent,
          listUrl: buildRequestListUrl(
            undefined,
            agent,
            REQUEST_TYPE_BY_KEY[key].service,
          ),
        }
      : null;
  return { navigation: inferred, setNavigation };
}
